import { isNum, limitDecimal } from '@utils/helpers';

/** Default number of buckets used for a property's distribution chart. */
export const DEFAULT_HISTOGRAM_BUCKETS = 10;

/**
 * Buckets a flat array of numeric values into `numBuckets` equal-width ranges
 * between `min` and `max`, counting how many values fall into each.
 *
 * Pure, framework-agnostic — used by PropertyHistogram to build chart.js data.
 * Non-numeric values are ignored. Values are clamped into the nearest valid
 * bucket (handles the edge case where a value equals `max`).
 *
 * @param {Array<number>} values - Raw property values (e.g. every feature's `height`)
 * @param {number} min
 * @param {number} max
 * @param {number} [numBuckets]
 * @returns {Array<{ min: number, max: number, count: number }>|null} - null when the range is invalid
 */
export const buildHistogramBuckets = (values, min, max, numBuckets = DEFAULT_HISTOGRAM_BUCKETS) => {
	if (!Number.isInteger(numBuckets) || numBuckets <= 0) return null;

	const range = max - min;
	if (!isNum(range) || !isFinite(range) || range <= 0) return null;

	const bucketSize = range / numBuckets;
	const buckets = [];
	for (let i = 0; i < numBuckets; i++) {
		buckets.push({
			min: limitDecimal(min + i * bucketSize),
			max: limitDecimal(min + (i + 1) * bucketSize),
			count: 0,
		});
	}

	for (const value of values ?? []) {
		if (!isNum(value)) continue;

		const bucketIndex = Math.floor((value - min) / bucketSize);
		const clampedIndex = Math.min(Math.max(bucketIndex, 0), numBuckets - 1);
		buckets[clampedIndex].count += 1;
	}

	return buckets;
};
